import { ref } from 'vue';

// 全局认证状态（跨组件共享单例）
const isAuthenticated = ref(false);
const isCheckingAuth = ref(true);
const currentUser = ref('');
const currentRole = ref('');
const sessionExpired = ref(false);
let sessionExpiredHandled = false;

// 全局 fetch 拦截器：捕获 401 过期响应，自动清理认证状态并提示用户
const originalFetch = window.fetch;
window.fetch = async function(...args) {
  const response = await originalFetch.apply(this, args);

  if (response.status === 401) {
    const url = (typeof args[0] === 'string') ? args[0] : args[0]?.url || '';
    if (url.includes('/api/login') || url.includes('/api/register')) {
      return response;
    }

    if (!sessionExpiredHandled) {
      sessionExpiredHandled = true;
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('user_role');
      localStorage.removeItem('username');
      isAuthenticated.value = false;
      currentRole.value = '';
      currentUser.value = '';
      sessionExpired.value = true;
    }

    return new Response(JSON.stringify({ status: 'error', message: '登录已过期，请重新登录' }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  return response;
};

export function useAuth() {
  const loginUsername = ref('');
  const loginPwd = ref('');
  const loginError = ref('');

  // 前端验证规则（与后端一致）
  const validateUsername = (name) => {
    if (!name || !name.trim()) return '用户名不能为空';
    const trimmed = name.trim();
    if (['null', 'none', 'nil', 'undefined', 'nan'].includes(trimmed.toLowerCase())) return '用户名不能为非法占位值';
    if (trimmed.length < 2) return '用户名至少需要2个字符';
    if (trimmed.length > 20) return '用户名不能超过20个字符';
    if (!/^[一-龥a-zA-Z0-9_-]+$/.test(trimmed)) return '用户名只能包含中文、字母、数字、下划线和短横线';
    return '';
  };

  const validatePassword = (pwd) => {
    if (!pwd) return '密码不能为空';
    if (pwd.length < 6) return '密码长度至少为6位';
    if (pwd.length > 50) return '密码不能超过50个字符';
    if (!/[a-zA-Z]/.test(pwd) || !/[0-9]/.test(pwd)) return '密码必须同时包含字母和数字';
    return '';
  };

  // 设置携带 token 的统一请求头生成器
  const getAuthHeaders = (isFormData = false) => {
    const token = localStorage.getItem('jwt_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('jwt_token');
      const role = localStorage.getItem('user_role');
      const username = localStorage.getItem('username');
      
      if (token && role && username) {
         isAuthenticated.value = true;
         currentRole.value = role;
         currentUser.value = username;
      } else {
         isAuthenticated.value = false;
      }
    } catch(e) {
      isAuthenticated.value = false;
    } finally {
      isCheckingAuth.value = false;
    }
  };

  const handleLogin = async () => {
    try {
      loginError.value = '';
      if (!loginUsername.value || !loginPwd.value) {
        loginError.value = '用户名和密码不能为空';
        return;
      }
      const nameErr = validateUsername(loginUsername.value);
      if (nameErr && !loginUsername.value.includes('@')) {
        loginError.value = nameErr;
        return;
      }
      const pwdErr = validatePassword(loginPwd.value);
      if (pwdErr) {
        loginError.value = pwdErr;
        return;
      }

      const res = await fetch('/api/login', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUsername.value, password: loginPwd.value })
      });
      const data = await res.json();
      
      if (data.status === 'success' && data.token && data.role) {
        localStorage.setItem('jwt_token', data.token);
        localStorage.setItem('user_role', data.role);
        localStorage.setItem('username', data.username || '');

        isAuthenticated.value = true;
        currentRole.value = data.role;
        currentUser.value = data.username || '';
        dismissSessionExpired();
      } else {
        loginError.value = data.message || '用户名或密码错误。';
      }
    } catch(e) {
       loginError.value = '服务器连接失败！';
    }
  };

  const handleRegister = async (regUsername, regEmail, regCode, regPwd) => {
    try {
      loginError.value = '';
      if (!regUsername || !regPwd || !regEmail || !regCode) {
        loginError.value = '请填写所有注册信息';
        return false;
      }
      const nameErr = validateUsername(regUsername);
      if (nameErr) { loginError.value = nameErr; return false; }
      const pwdErr = validatePassword(regPwd);
      if (pwdErr) { loginError.value = pwdErr; return false; }

      const res = await fetch('/api/register', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: regUsername, email: regEmail, code: regCode, password: regPwd })
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        // 注册成功后自动帮他填入登录框
        loginUsername.value = regUsername;
        loginPwd.value = regPwd;
        return true;
      } else {
        loginError.value = data.message || '注册失败。';
        return false;
      }
    } catch(e) {
       loginError.value = '服务器连接失败！';
       return false;
    }
  };

  const dismissSessionExpired = () => {
    sessionExpired.value = false;
    sessionExpiredHandled = false;
  };

  const handleLogout = () => {
     localStorage.removeItem('jwt_token');
     localStorage.removeItem('user_role');
     localStorage.removeItem('username');
     isAuthenticated.value = false;
     currentRole.value = '';
     currentUser.value = '';
     dismissSessionExpired();
  };

  return {
    isAuthenticated,
    isCheckingAuth,
    currentUser,
    currentRole,
    sessionExpired,
    loginUsername,
    loginPwd,
    loginError,
    checkAuth,
    handleLogin,
    handleRegister,
    handleLogout,
    dismissSessionExpired,
    getAuthHeaders
  };
}
